import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { enviroment } from '../../environments/environment';
import {
  GroupedNotification,
  GroupedNotificationActor,
  GroupedNotificationPage,
  Notification,
  NotificationPage,
  NotificationSseEvent,
  NotificationType,
  UnreadCountResponse
} from '../model/notification.model';

function numOrUndef(v: unknown): number | undefined {
  if (v === undefined || v === null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function normalizeAggregatedItem(raw: Record<string, unknown>): GroupedNotification {
  const actorsRaw = (raw['actors'] ?? raw['Actors'] ?? []) as Record<string, unknown>[];
  const actors: GroupedNotificationActor[] = actorsRaw.map((a) => ({
    userId: a['userId'] !== undefined ? String(a['userId']) : a['UserId'] !== undefined ? String(a['UserId']) : undefined,
    nickname: (a['nickname'] ?? a['Nickname']) as string | null | undefined,
  }));
  const gk = raw['groupKey'] !== undefined ? raw['groupKey'] : raw['GroupKey'];
  const meta = raw['metadata'] !== undefined ? raw['metadata'] : raw['Metadata'];
  const ids = raw['notificationIds'] ?? raw['NotificationIds'];
  const notificationIds = Array.isArray(ids) ? (ids as unknown[]).map(String) : [];

  return {
    groupKey: gk === undefined || gk === null ? null : String(gk),
    notificationIds,
    type: String(raw['type'] ?? raw['Type'] ?? 'info'),
    title: String(raw['title'] ?? raw['Title'] ?? ''),
    message: (raw['message'] ?? raw['Message']) as string | null | undefined,
    body: (raw['body'] ?? raw['Body']) as string | null | undefined,
    actors,
    totalActors: Number(raw['totalActors'] ?? raw['TotalActors'] ?? 0),
    isRead: Boolean(raw['isRead'] ?? raw['IsRead']),
    createdAt: String(raw['createdAt'] ?? raw['CreatedAt'] ?? ''),
    metadata: meta == null ? null : (meta as Record<string, unknown>),
  };
}

function normalizeGroupedPage(raw: Record<string, unknown>): GroupedNotificationPage {
  const list = (raw['listItens'] ?? raw['ListItens'] ?? []) as unknown[];
  return {
    currentPage: Number(raw['currentPage'] ?? raw['CurrentPage'] ?? 1),
    listItens: list.map((item) =>
      normalizeAggregatedItem(item != null && typeof item === 'object' ? (item as Record<string, unknown>) : {})
    ),
    totalPages: numOrUndef(raw['totalPages'] ?? raw['TotalPages']),
    itensPage: numOrUndef(raw['itensPage'] ?? raw['ItensPage'] ?? raw['pageSize'] ?? raw['PageSize']),
    totalItens: numOrUndef(raw['totalItens'] ?? raw['TotalItens']),
  };
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService implements OnDestroy {

  private eventSource: EventSource | null = null;
  private reconnectTimer: any = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 20;
  private baseReconnectDelay = 2000;

  private _notifications$ = new Subject<NotificationSseEvent>();
  private _unreadCount$ = new BehaviorSubject<number>(0);
  private _connectionStatus$ = new BehaviorSubject<'disconnected' | 'connecting' | 'connected'>('disconnected');

  public notifications$ = this._notifications$.asObservable();
  public unreadCount$ = this._unreadCount$.asObservable();
  public connectionStatus$ = this._connectionStatus$.asObservable();

  private readonly apiUrl = enviroment.BASE_URL_API;
  private readonly sseUrl = enviroment.BASE_URL_SSE;

  constructor(
    private http: HttpClient,
    private ngZone: NgZone
  ) {}

  // =============================================
  //  SSE — CONEXAO EM TEMPO REAL
  // =============================================

  connect(userId: string): void {
    if (this.eventSource) {
      this.disconnect();
    }

    this._connectionStatus$.next('connecting');
    this.reconnectAttempts = 0;
    this.createEventSource(userId);
    this.refreshUnreadCount(userId);
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this._connectionStatus$.next('disconnected');
  }

  private createEventSource(userId: string): void {
    const url = `${this.sseUrl}/streaming/sse/notifications/${userId}`;
    this.eventSource = new EventSource(url);

    this.eventSource.addEventListener('connected', (event: MessageEvent) => {
      this.ngZone.run(() => {
        this._connectionStatus$.next('connected');
        this.reconnectAttempts = 0;
        console.log('[Notifications] SSE conectado', JSON.parse(event.data));
      });
    });

    this.eventSource.addEventListener('notification', (event: MessageEvent) => {
      this.ngZone.run(() => {
        try {
          const notification: NotificationSseEvent = JSON.parse(event.data);
          this._notifications$.next(notification);
          this._unreadCount$.next(this._unreadCount$.value + 1);
        } catch (err) {
          console.error('[Notifications] Erro ao parsear notificacao:', err);
        }
      });
    });

    this.eventSource.addEventListener('heartbeat', () => {});

    this.eventSource.onerror = () => {
      this.ngZone.run(() => {
        console.warn('[Notifications] SSE erro/desconexao');
        this._connectionStatus$.next('disconnected');
        this.eventSource?.close();
        this.eventSource = null;
        this.scheduleReconnect(userId);
      });
    };
  }

  private scheduleReconnect(userId: string): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[Notifications] Max tentativas de reconexao atingido');
      return;
    }

    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      30000
    );
    this.reconnectAttempts++;

    console.log(`[Notifications] Reconectando em ${delay}ms (tentativa ${this.reconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      this._connectionStatus$.next('connecting');
      this.createEventSource(userId);
    }, delay);
  }

  // =============================================
  //  REST API — HISTORICO E ACOES
  // =============================================

  getNotifications(
    userId: string,
    page = 1,
    pageItens = 20,
    isRead?: boolean,
    type?: NotificationType
  ): Observable<NotificationPage> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageItens', pageItens.toString());

    if (isRead !== undefined) {
      params = params.set('isRead', isRead.toString());
    }
    if (type) {
      params = params.set('type', type);
    }

    return this.http.get<NotificationPage>(
      `${this.apiUrl}/api/user/${userId}/notifications`,
      { params }
    );
  }

  getUnreadCount(userId: string): Observable<UnreadCountResponse> {
    return this.http.get<UnreadCountResponse>(
      `${this.apiUrl}/api/user/${userId}/notifications/unread-count`
    );
  }

  refreshUnreadCount(userId: string): void {
    this.getUnreadCount(userId).subscribe({
      next: (res) => this._unreadCount$.next(res.unreadCount),
      error: (err) => console.error('[Notifications] Erro ao buscar unread count:', err)
    });
  }

  markAsRead(userId: string, notificationId: string): Observable<{ success: boolean }> {
    return this.http.patch<{ success: boolean }>(
      `${this.apiUrl}/api/user/${userId}/notifications/${notificationId}/read`,
      {}
    );
  }

  markAllAsRead(userId: string): Observable<{ success: boolean }> {
    return this.http.patch<{ success: boolean }>(
      `${this.apiUrl}/api/user/${userId}/notifications/read-all`,
      {}
    );
  }

  /**
   * Lista notificações agrupadas (social + flat). Ver docs/notifications_new.md
   * GET /api/user/{userId}/notifications/social/aggregated?page=&pageSize=
   */
  getGroupedNotifications(
    userId: string,
    page = 1,
    pageSize = 20
  ): Observable<GroupedNotificationPage> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));
    return this.http
      .get<Record<string, unknown>>(
        `${this.apiUrl}/api/user/${userId}/notifications/social/aggregated`,
        { params }
      )
      .pipe(map((raw) => normalizeGroupedPage(raw)));
  }

  /**
   * Marca várias notificações como lidas em uma única chamada.
   * PATCH /api/user/{userId}/notifications/read-batch
   */
  /** Resposta esperada: 204 No Content. */
  markBatchAsRead(userId: string, notificationIds: string[]): Observable<void> {
    return this.http.patch<void>(
      `${this.apiUrl}/api/user/${userId}/notifications/read-batch`,
      { notificationIds },
      { observe: 'response' }
    ).pipe(
      // 204 sem corpo — ignora o body
      map(() => undefined)
    );
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
